import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, EyeOff, ChevronRight, Lock, Globe, Zap, Server, Mail, Phone } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/context/ToastContext';
import { apiService } from '@/services/api/apiService';

// ---- Minimal motion helpers (unchanged) ----
const style = `
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}
.animate-blob { animation: blob 7s infinite; }
.animation-delay-2000 { animation-delay: 2s; }
.animation-delay-4000 { animation-delay: 4s; }
`;

// ---- Env ----
const PORT = import.meta.env.VITE_IAM_PORT || '8080';
const IAMURL = import.meta.env.VITE_API_IAM_URL || '/api/v1/tiffit/idam';

// ---- Types / helpers ----
type UUID = string;
type LoginType = 'email' | 'mobile';

interface RawSession {
  error: any;
  message: any;
  userProfileId: string;
  status: 'ok' | 'error';
  deviceId: string | null;
  jwtToken: string;
  username: string;
  expiresAt: string;
  loginType: LoginType;
  authUserId: UUID;
  authRoleIds: number[];
  signinInfoId: UUID;
  signinMethod: 'password' | 'otp';
  userSessionId: UUID;
}
interface ApiEnvelope<T = any> {
  isSuccess?: boolean;
  status?: number;
  data?: T;
  message?: string;
}
type Role = { id: number; name: string };

function unwrap<T = any>(res: any): T {
  const p = res?.data ?? res;
  return (p?.data ?? p) as T;
}
const getAuthUserId = (): string | null => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.authUserId || null;
  } catch {
    return null;
  }
};
function getClientInfo() {
  const platform = (navigator as any)?.platform ?? 'web';
  const model = `${navigator.appName} on ${platform} | ${navigator.userAgent}`;
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    if (typeof crypto.randomUUID === 'function') {
      // Modern browsers
      deviceId = crypto.randomUUID();
    } else {
      // Fallback for older browsers
      deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }
    localStorage.setItem('deviceId', deviceId);
  }

  return { deviceId, platform: 'web', model };
}
interface UserMenu {}
async function postLoginBootstrap(session: RawSession) {
  const authUserId = getAuthUserId();
  const [menuEnv] = await Promise.all([
    apiService.post(
      '/get_user_menu',
      { authUserId }, // ✅ MUST NOT be empty
      PORT,
      IAMURL,
    ),
  ]);

  const menu = unwrap<UserMenu>(menuEnv);
  localStorage.setItem('userMenu', JSON.stringify(menu));
}
function persistSession(s: RawSession) {
  localStorage.setItem('authToken', s.jwtToken);
  localStorage.setItem('authUserId', s.authUserId);
  localStorage.setItem('authUsername', s.username);
  localStorage.setItem('authExpiresAt', s.expiresAt);
  localStorage.setItem('authRoles', JSON.stringify(s.authRoleIds || []));
  localStorage.setItem('userSessionId', s.userSessionId);
  localStorage.setItem('userProfileId', s.userProfileId);
}

// ---------- UI bits (tabs + otp boxes) ----------
const SegmentedTabs: React.FC<{
  value: LoginType;
  onChange: (v: LoginType) => void;
}> = ({ value, onChange }) => {
  const opts = [
    { key: 'email' as const, label: 'Email', icon: Mail },
    { key: 'mobile' as const, label: 'Mobile', icon: Phone },
  ];
  return (
    <div
      role='tablist'
      aria-label='Login method'
      className='inline-flex rounded-xl border border-gray-200 bg-white/80 backdrop-blur'
    >
      {opts.map((o) => {
        const active = value === o.key;
        const Icon = o.icon;
        return (
          <button
            role='tab'
            aria-selected={active}
            key={o.key}
            onClick={() => onChange(o.key)}
            className={[
              'px-4 py-2 text-sm font-medium flex items-center gap-2 rounded-xl transition-all',
              active ? 'bg-emerald-600 text-white shadow-inner' : 'text-gray-700 hover:bg-gray-50',
            ].join(' ')}
          >
            <Icon size={16} className={active ? 'text-white' : 'text-gray-600'} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const OTPBoxes: React.FC<{
  length?: number;
  onSubmit: (code: string) => void;
  isLoading?: boolean;
  error?: string;
}> = ({ length = 6, onSubmit, isLoading, error }) => {
  const [vals, setVals] = useState<string[]>(Array(length).fill(''));
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const setAt = (i: number, v: string) => {
    const next = [...vals];
    next[i] = v;
    setVals(next);
  };

  const handleChange = (i: number, v: string) => {
    const only = v.replace(/\D/g, '').slice(-1);
    if (!only) {
      setAt(i, '');
      return;
    }
    setAt(i, only);
    if (i < length - 1) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const clip = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!clip) return;
    const next = clip.split('');
    for (let i = 0; i < length; i++) setAt(i, next[i] || '');
    refs.current[Math.min(clip.length, length) - 1]?.focus();
  };

  const code = vals.join('');
  return (
    <>
      <div className='flex justify-center gap-2'>
        {vals.map((v, i) => (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            value={v}
            onChange={(e) => handleChange(i, e.target.value)}
            onPaste={i === 0 ? handlePaste : undefined}
            inputMode='numeric'
            pattern='\d*'
            maxLength={1}
            aria-label={`OTP digit ${i + 1}`}
            className={[
              'w-12 h-12 text-center text-xl font-mono rounded-xl border-2 bg-white/80',
              'focus:outline-none focus:ring-2 focus:border-transparent transition-all',
              error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-200 focus:ring-emerald-600',
            ].join(' ')}
          />
        ))}
      </div>

      {error && <p className='pt-2 text-center text-sm text-red-600'>{error}</p>}

      <button
        type='button'
        disabled={isLoading || code.length !== length}
        onClick={() => onSubmit(code)}
        className='mt-4 w-full flex justify-center items-center px-4 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 transition-all disabled:opacity-50'
      >
        {isLoading ? (
          <>
            <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2' />
            Verifying...
          </>
        ) : (
          <>Verify OTP</>
        )}
      </button>
    </>
  );
};

// ---- OTP screen ----
const OTPScreen: React.FC<{
  identifier: string;
  loginType: LoginType;
  onVerify: (otp: string) => void;
  isLoading: boolean;
  error: string;
  onBackToLogin: () => void;
}> = ({ identifier, loginType, onVerify, isLoading, error, onBackToLogin }) => {
  const [seconds, setSeconds] = useState(30);
  useEffect(() => {
    if (seconds <= 0) return;
    const t = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [seconds]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className='bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-gray-200 w-full max-w-md mx-auto'
      style={{ boxShadow: '0 25px 50px rgba(64, 82, 29, 0.15)' }}
    >
      <div className='space-y-3 text-center mb-4'>
        <div className='flex justify-center'>
          <img
            src='/images/TiffitLogo.png'
            alt='TIFFIT'
            className='h-[100px] w-[260px] object-contain drop-shadow'
          />
        </div>
        <h1 className='text-2xl font-bold tracking-tight text-gray-900'>Enter OTP</h1>
        <p className='text-sm text-gray-600'>
          We’ve sent a one-time password to{' '}
          <span className='font-semibold' style={{ color: '#40521d' }}>
            {loginType === 'email' ? 'email' : 'mobile'}: {identifier}
          </span>
        </p>
      </div>

      <OTPBoxes onSubmit={onVerify} isLoading={isLoading} error={error} />

      <div className='mt-4 flex items-center justify-between text-sm'>
        <button
          type='button'
          onClick={onBackToLogin}
          className='px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition'
        >
          Back to Login
        </button>

        <button
          type='button'
          disabled={seconds > 0}
          onClick={() => {
            window.dispatchEvent(new CustomEvent('tiffit:resend-otp'));
            setSeconds(30);
          }}
          className={[
            'px-3 py-2 rounded-lg border',
            seconds > 0
              ? 'text-gray-400 border-gray-200 cursor-not-allowed'
              : 'text-emerald-700 border-emerald-600 hover:bg-emerald-50',
          ].join(' ')}
        >
          {seconds > 0 ? `Resend in ${seconds}s` : 'Resend OTP'}
        </button>
      </div>

      <p className='w-full text-center mt-4 text-xs text-gray-400'>V.1.3</p>
    </motion.div>
  );
};

// ---- Main ----
export default function LoginPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loginType, setLoginType] = useState<LoginType>('email');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeInput, setActiveInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const emailValid = useMemo(() => /\S+@\S+\.\S+/.test(username), [username]);
  const mobileValid = useMemo(() => /^[0-9+\-\s()]{8,}$/.test(username), [username]);
  const idLabel = loginType === 'email' ? 'Email address' : 'Mobile number';
  const idPlaceholder = loginType === 'email' ? 'you@example.com' : 'Enter your mobile';

  const canPasswordSubmit =
    !!username &&
    (loginType === 'email' ? emailValid : mobileValid) &&
    !!password &&
    password.length >= 6;

  useEffect(() => {
    const h = () => startOtpFlow();
    window.addEventListener('tiffit:resend-otp', h);
    return () => window.removeEventListener('tiffit:resend-otp', h);
  }, [username, loginType]);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canPasswordSubmit) {
      showToast({ type: 'error', message: 'Please enter valid credentials.' });
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiService.post<ApiEnvelope<RawSession>>(
        '/login',
        {
          username,
          password,
          loginType,
          passwordVerified: true,
          adminLogin: true,
          ...getClientInfo(),
        },
        PORT,
        IAMURL,
      );

      const data = unwrap<RawSession>(res);
      localStorage.setItem('user', JSON.stringify(data));
      if (data?.status === 'ok' && data?.jwtToken && data?.authUserId) {
        persistSession(data);
        await postLoginBootstrap(data);
        showToast({ type: 'success', message: 'Login successful' });
        navigate('/*');
      } else {
        const backendError = data?.error || data?.message || 'Invalid credentials';
        showToast({ type: 'error', message: backendError });
      }
    } catch (err: any) {
      const backendError =
        err?.response?.data?.data?.error || // <--- your backend sends it here
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Unable to sign in.';

      showToast({ type: 'error', message: backendError });
    } finally {
      setIsLoading(false);
    }
  };
  // 1) Send OTP for EMAIL (purpose: Login)
  const startOtpFlow = async () => {
    if (!username || (loginType === 'email' ? !emailValid : !mobileValid)) {
      showToast({ type: 'error', message: `Please enter a valid ${loginType}.` });
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      const payload = {
        username,
        loginType,
        otpPurposeName: 'Login',
        ttlSeconds: 300,
        otpLength: 6,
        notify: true,
      };
      const res = await apiService.post('/issue_otp', payload, PORT, IAMURL);
      const data = unwrap<any>(res);
      if ((data?.status ?? 'ok') === 'ok') {
        setShowOtpScreen(true);
        showToast({ type: 'success', message: 'OTP sent to your email' });
      } else {
        showToast({ type: 'error', message: 'Failed to send OTP' });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to send OTP.';
      showToast({ type: 'error', message: msg });
    } finally {
      setOtpLoading(false);
    }
  };

  // 2) VERIFY on OTP screen -> CREATE SESSION via /login
  const handleOtpVerify = async (code: string) => {
    setOtpLoading(true);
    setOtpError('');
    try {
      const res = await apiService.post<ApiEnvelope<RawSession>>(
        '/login',
        {
          username,
          loginType, // "email" in your case here
          signinMethodName: 'otp',
          otp: code,
          otpPurposeName: 'Login',
          ...getClientInfo(),
        },
        PORT,
        IAMURL,
      );
      const data = unwrap<RawSession>(res);
      localStorage.setItem('user', JSON.stringify(data));

      if (data?.status === 'ok' && data?.jwtToken && data?.authUserId) {
        persistSession(data);
        await postLoginBootstrap(data); // <-- add this
        showToast({ type: 'success', message: 'Login successful' });
        navigate('/*');
      } else {
        setOtpError('Invalid or expired OTP. Please try again.');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'OTP login failed.';
      setOtpError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowOtpScreen(false);
    setOtpError('');
  };

  // shared input classes tuned to your palette
  const inputBase = [
    'w-full pl-10 pr-4 py-3 bg-white/70 backdrop-blur-sm border-2 rounded-xl',
    'focus:outline-none focus:ring-2 focus:border-transparent transition-all text-gray-900 placeholder:text-gray-500',
    'border-gray-200 focus:ring-emerald-600',
  ].join(' ');

  return (
    <>
      <style>{style}</style>
      <div
        className='min-h-screen w-full flex overflow-hidden items-center justify-center p-4'
        style={{ background: 'linear-gradient(135deg, #deeddf 0%, #9dad5e 100%)' }}
      >
        <div className='flex-1 flex relative z-10 max-w-[1200px] w-full'>
          {/* Left: Story / USP */}
          <div className='hidden lg:flex lg:w-[60%] items-center justify-center px-8'>
            <div className='max-w-3xl'>
              <div className='flex justify-center'>
                <img
                  src='/images/logo.png'
                  alt='Tiffit Logo'
                  className='h-[140px] w-[370px] object-contain'
                />
              </div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className='text-center mb-8'
              >
                <h2 className='text-5xl font-bold tracking-tight text-gray-900 mb-4'>
                  Home Cook Food
                  <br />
                  <span className='text-emerald-700'> Platform</span>
                </h2>
                <p className='text-lg leading-8 text-gray-800/90'>
                  Bringing authentic home-cooked meals from passionate cooks to your table. Fresh,
                  rewarding, and community-driven.
                </p>
              </motion.div>

              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className='grid grid-cols-2 gap-4 mb-8'
              >
                {[
                  {
                    icon: Lock,
                    title: 'Authentic Taste',
                    desc: 'Meals crafted with love & tradition',
                  },
                  {
                    icon: Globe,
                    title: 'Community Impact',
                    desc: 'Support local cooks & families',
                  },
                  { icon: Zap, title: 'TIFFIT Points', desc: 'Earn & redeem with every order' },
                  {
                    icon: Server,
                    title: 'Smart & Sustainable',
                    desc: 'AI-driven routing, eco-friendly',
                  },
                ].map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 + idx * 0.1 }}
                    className='group p-4 bg-white/70 backdrop-blur-sm rounded-xl hover:bg-white transition-all duration-300 border border-gray-200'
                    style={{ boxShadow: '0 10px 20px rgba(64, 82, 29, 0.08)' }}
                  >
                    <feature.icon className='w-6 h-6 text-emerald-700 group-hover:text-emerald-800 transition-colors mb-2' />
                    <h3 className='text-sm font-semibold text-gray-900 mb-1'>{feature.title}</h3>
                    <p className='text-xs text-gray-600'>{feature.desc}</p>
                  </motion.div>
                ))}
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className='text-center text-emerald-800 text-lg font-medium italic tracking-wide'
              >
                Cooked with care. Delivered with love.
              </motion.p>
            </div>
          </div>

          {/* Right: Auth */}
          <div className='w-full lg:w-2/3 flex items-center justify-center px-4 sm:px-6 lg:px-8 ml-auto'>
            <AnimatePresence mode='wait'>
              {!showOtpScreen ? (
                <motion.div
                  key='login'
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.5 }}
                  className='w-full'
                >
                  <div className='w-full max-w-md'>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className='bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-gray-200'
                      style={{ boxShadow: '0 25px 50px rgba(64, 82, 29, 0.15)' }}
                    >
                      {/* Logo + Title */}
                      <div className='space-y-3 text-center mb-4'>
                        <h1 className='text-3xl font-bold tracking-tight text-gray-900'>
                          Welcome Back
                        </h1>
                        <p className='text-sm text-gray-600'>
                          Secure access to your Home Cook Food platform
                        </p>
                      </div>

                      {/* Tab-like selector */}
                      <div className='mb-5 flex justify-center'>
                        <SegmentedTabs
                          value={loginType}
                          onChange={(v) => {
                            setLoginType(v);
                            setUsername('');
                          }}
                        />
                      </div>

                      {/* Username */}
                      <div className='space-y-2 mb-4'>
                        <label className='block text-sm font-medium' style={{ color: '#40521d' }}>
                          {idLabel}
                        </label>

                        <div className='relative'>
                          <div className='absolute top-1/2 -translate-y-1/2 left-4 text-gray-400 z-10'>
                            {loginType === 'email' ? <Mail size={18} /> : <Phone size={18} />}
                          </div>

                          <input
                            type={loginType === 'email' ? 'email' : 'tel'}
                            name='username'
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value.trim())}
                            onFocus={() => setActiveInput('username')}
                            className={[
                              inputBase, // must include pl-10
                              activeInput === 'username' ? 'scale-[1.01]' : '',
                            ].join(' ')}
                            placeholder={idPlaceholder}
                          />
                        </div>

                        {username && loginType === 'email' && !emailValid && (
                          <p className='pt-1 text-red-500 text-sm'>Please enter a valid email</p>
                        )}
                        {username && loginType === 'mobile' && !mobileValid && (
                          <p className='pt-1 text-red-500 text-sm'>Please enter a valid mobile</p>
                        )}
                      </div>

                      {/* Password Login */}
                      <form onSubmit={submitPassword} className='space-y-6'>
                        <div className='space-y-2'>
                          <label className='block text-sm font-medium' style={{ color: '#40521d' }}>
                            Password
                          </label>

                          <div className='relative'>
                            {/* Lock icon */}
                            <div className='absolute top-1/2 -translate-y-1/2 left-4 text-gray-400 z-10'>
                              <Lock size={18} />
                            </div>

                            <input
                              type={showPassword ? 'text' : 'password'}
                              name='password'
                              required
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              onFocus={() => setActiveInput('password')}
                              className={[
                                inputBase, // MUST contain pl-10
                                'pr-10', // space for eye icon
                                activeInput === 'password' ? 'scale-[1.01]' : '',
                              ].join(' ')}
                              placeholder='Enter your password'
                            />

                            {/* Show / hide button */}
                            <button
                              type='button'
                              onClick={() => setShowPassword((s) => !s)}
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              className='absolute top-1/2 -translate-y-1/2 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10'
                            >
                              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                          </div>

                          {password && password.length < 6 && (
                            <p className='pt-1 text-red-500 text-sm'>
                              Password must be at least 6 characters
                            </p>
                          )}
                        </div>
                        <div className='flex items-center justify-between'>
                          <label className='flex items-center'>
                            <input
                              type='checkbox'
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              className='w-4 h-4 text-emerald-700 bg-white border-gray-300 rounded focus:ring-emerald-700 focus:ring-2'
                            />
                            <span className='ml-2 text-sm text-gray-700'>Remember me</span>
                          </label>
                        </div>

                        {/* Submit (Password) */}
                        <button
                          type='submit'
                          disabled={isLoading || !canPasswordSubmit}
                          className='w-full flex justify-center items-center px-4 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 transition-all disabled:opacity-50'
                        >
                          {isLoading ? (
                            <>
                              <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2' />
                              Signing in...
                            </>
                          ) : (
                            <>
                              Sign in <ChevronRight size={20} className='ml-2' />
                            </>
                          )}
                        </button>

                        {/* Divider */}
                        <div className='flex items-center my-2'>
                          <div className='flex-1 border-t border-gray-200' />
                          <span className='px-4 text-gray-500 text-sm'>or</span>
                          <div className='flex-1 border-t border-gray-200' />
                        </div>

                        {/* OTP Entry Button */}
                        <button
                          type='button'
                          onClick={startOtpFlow}
                          disabled={otpLoading}
                          className='w-full flex justify-center items-center px-4 py-3 bg-white text-emerald-700 border-2 border-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 transition-all'
                        >
                          {otpLoading ? 'Sending OTP…' : 'Sign in with OTP'}
                        </button>
                      </form>
                    </motion.div>
                  </div>
                </motion.div>
              ) : (
                <OTPScreen
                  key='otp'
                  identifier={username}
                  loginType={loginType}
                  onVerify={handleOtpVerify}
                  isLoading={otpLoading}
                  onBackToLogin={handleBackToLogin}
                  error={otpError}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
}
